import { Component, Element, Event, EventEmitter, h, Method, Prop, State, Watch } from '@stencil/core';
import Bind from 'bind-decorator';

import { LinkTarget, StyleCollection } from '../../types';
import { AriaObject, processAriaObject } from '../../utils/aria-object';
import { getAdditionalPaddingFromBorderRadii } from '../../utils/border-radius-additional-padding';
import { capitalize } from '../../utils/capitalize';
import { hasContentAfter, hasContentBefore } from '../../utils/dom';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { getBorderRadiiFromTokens } from '../../utils/get-border-radius';
import { createMutationObserver } from '../../utils/mutation-observer';
import { parseSpecialBooleanProp } from '../../utils/special-boolean-prop';

@Component({
	shadow: true,
	styleUrl: 'zds-button.scss',
	tag: 'zds-button',
})
export class ZdsButton {
	buttonElement: HTMLButtonElement;
	forcedActiveStateTimeout: NodeJS.Timeout;
	intersectionObserver: IntersectionObserver;
	overhangArray: string[];
	mutationObserver: MutationObserver;

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter<FocusEvent>;
	@Event({ bubbles: true }) zdsClick: EventEmitter<MouseEvent>;
	@Event() zdsFocus: EventEmitter<FocusEvent>;

	// Private events for form execution, these should _not_ be documented or used by non-zds functionality as the
	//	behavior might change in the future
	@Event({ bubbles: true }) zdsFormDoReset: EventEmitter;
	@Event({ bubbles: true }) zdsFormDoSubmit: EventEmitter;

	@Prop() aspectSquare: boolean;
	@Prop() autoSubmit: number;
	@Prop() ariaLabel: string;
	@Prop({ reflect: true, mutable: true }) disabled: boolean;
	@Prop() fitHeight: boolean = false;
	@Prop() inputAria: AriaObject | string;
	@Prop() link: string;
	@Prop() linkTarget: LinkTarget = '_self';
	@Prop() overhang: string[] | string = [];
	@Prop({ reflect: true }) pill: boolean;
	@Prop({ reflect: true }) preset:
		| 'primary'
		| 'secondary'
		| 'accent'
		| 'neutral'
		| 'danger'
		| 'info'
		| 'success'
		| 'loud'
		| 'muted'
		| 'white'
		| 'custom'
		| 'inherit' = 'primary';
	@Prop({ reflect: true }) size: 'sm' | 'md' | 'lg' = 'md';
	@Prop() type: 'default' | 'submit' | 'reset' = 'default';
	@Prop({ reflect: true }) variant: 'contained' | 'outlined' | 'text' = 'contained';

	@State() buttonStyle: StyleCollection;
	@State() countdownPercentage: number = 100;
	@State() countdownSeconds: number;
	@State() hasForcedActiveState: boolean;
	@State() isStyleReady: boolean;
	@State() wrapperStyle: StyleCollection;

	@Method()
	async focusComponent() {
		this.buttonElement.focus();
	}

	@Watch('overhang')
	onOverhangChange() {
		this.updateOverhangArray();
	}

	@Watch('disabled')
	onDisabledToggle() {
		const val = parseSpecialBooleanProp(this.disabled);
		if (!val) {
			this.hostElement.removeAttribute('disabled');
		}
	}

	@Bind
	onBlur(event: FocusEvent) {
		event.stopPropagation();
		this.zdsBlur.emit(event);
	}

	@Bind
	onClick(event: MouseEvent | null) {
		if (this.disabled) {
			return;
		}

		event?.stopPropagation();
		this.zdsClick.emit(event);

		if (this.type !== 'default' && this[`zdsFormDo${capitalize(this.type)}`]) {
			this[`zdsFormDo${capitalize(this.type)}`].emit();
		}
	}

	@Bind
	onKeyDown(event: KeyboardEvent) {
		if (event.key !== 'Enter' || this.disabled) return;

		event.stopPropagation();
		this.onClick(null);
	}

	@Bind
	onMouseDown(event: MouseEvent) {
		// Ignore other buttons than left click
		if (event.button !== 0) return;

		// Force an active state for a while after the user pressed mouse down
		clearTimeout(this.forcedActiveStateTimeout);
		this.hasForcedActiveState = true;

		this.forcedActiveStateTimeout = setTimeout(() => {
			this.hasForcedActiveState = false;
		}, 300);
	}

	@Bind
	onFocus(event: FocusEvent) {
		event.stopPropagation();
		this.zdsFocus.emit(event);
	}

	@Bind
	calculateIconSpacing() {
		this.hostElement
			.querySelectorAll(':scope > zds-icon, :scope > .icon, :scope > .fa, :scope > [class^="fa-"]')
			.forEach((icon) => {
				icon.classList.add('zds-button__icon');
				icon.classList.toggle('zds-button__icon--has-content-after', hasContentAfter(icon));
				icon.classList.toggle('zds-button__icon--has-content-before', hasContentBefore(icon));
			});
	}

	componentDidLoad() {
		this.calculateIconSpacing();

		this.updateStyles();
		this.intersectionObserver = new IntersectionObserver(this.handleIntersection);

		this.mutationObserver = createMutationObserver(this.hostElement, this.calculateIconSpacing, {
			childList: true,
		});

		this.intersectionObserver.observe(this.hostElement);

		if (this.hasAutoSubmit()) {
			this.startCountdown();
		}

		this.onDisabledToggle();
	}

	componentWillLoad() {
		this.updateOverhangArray();
	}

	disconnectedCallback() {
		this.intersectionObserver?.disconnect();
		this.mutationObserver?.disconnect();
	}

	@Bind
	handleIntersection() {
		this.updateStyles();
	}

	hasAutoSubmit() {
		return this.autoSubmit && this.variant === 'contained';
	}

	render() {
		let ComponentTag = 'button';
		let attributes = {};

		const buttonClasses = generateCssClasses('zds-button', {
			[`variant-${this.variant}`]: true,
			[`size-${this.size}`]: true,
			aspectSquare: this.aspectSquare,
			autoSubmit: this.hasAutoSubmit(),
			fitHeight: this.fitHeight,
			overhangBottom: this.overhangArray.includes('bottom'),
			overhangLeft: this.overhangArray.includes('left'),
			overhangRight: this.overhangArray.includes('right'),
			overhangTop: this.overhangArray.includes('top'),
			pill: this.pill,
			ready: this.isStyleReady,
			wasJustClicked: this.hasForcedActiveState,
		});

		const countdownBackgroundInnerStyle = {
			animationDuration: this.autoSubmit + 's',
		};

		if (this.link && !this.disabled) {
			ComponentTag = 'a';
			attributes = {
				href: this.link,
				target: this.linkTarget,
			};
		}

		const inputAriaProps = processAriaObject(this.inputAria, 'zds-button', 'input-aria');

		return (
			<ComponentTag
				class={buttonClasses}
				{...attributes}
				disabled={this.disabled}
				onBlur={this.onBlur}
				onClick={this.onClick}
				onFocus={this.onFocus}
				onKeyDown={this.onKeyDown}
				onMouseDown={this.onMouseDown}
				ref={(buttonElement: HTMLButtonElement) => (this.buttonElement = buttonElement)}
				style={this.buttonStyle}
				{...inputAriaProps}
			>
				<div class="zds-button__background"></div>
				<div class="zds-button__background-hover"></div>
				<div class="zds-button__border"></div>
				<div class="zds-button__countdown-background">
					<div class="zds-button__countdown-background-inner" style={countdownBackgroundInnerStyle}></div>
				</div>
				<div class="zds-button__wrapper" style={this.wrapperStyle}>
					<slot />
					{this.hasAutoSubmit() && <span>&nbsp;({this.countdownSeconds}s)</span>}
				</div>
			</ComponentTag>
		);
	}

	startCountdown() {
		this.countdownSeconds = this.autoSubmit;
		this.countdownPercentage = 0;

		const interval = setInterval(() => {
			this.countdownPercentage += 100 / this.autoSubmit;
			this.countdownSeconds--;

			if (this.countdownPercentage >= 100) {
				this.countdownSeconds = 0; // Prevent negative values for non-integer durations
				clearInterval(interval);
				this.zdsFormDoSubmit.emit();
			}
		}, 1000);
	}

	updateOverhangArray() {
		// Parse `overhang` property to an array if it isn't one yet
		this.overhangArray =
			typeof this.overhang === 'string' ? this.overhang.replace(/ /g, '').split(',') : this.overhang;

		if (this.overhangArray.includes('all')) {
			this.overhangArray.push(...['bottom', 'left', 'right', 'top']);
		} else {
			if (this.overhangArray.includes('horizontal')) {
				this.overhangArray.push(...['left', 'right']);
			}
			if (this.overhangArray.includes('vertical')) {
				this.overhangArray.push(...['bottom', 'top']);
			}
		}
	}

	updateStyles() {
		requestAnimationFrame(() => {
			if (this.aspectSquare) {
				// Square buttons do not need additional padding or a special border radius
				this.buttonStyle = undefined;
				this.wrapperStyle = undefined;
				this.isStyleReady = true;
				return;
			}

			const borderRadii = getBorderRadiiFromTokens(this.hostElement, '--zds-button');
			if (borderRadii === null) return;

			const additionalPadding = getAdditionalPaddingFromBorderRadii(borderRadii);

			// Increase horizontal negative margin if overhanging left/right
			const buttonMarginLeft = this.overhangArray.includes('left')
				? `calc(-1 * var(--zds-button-inset-x-modified) - ${additionalPadding.left}px)`
				: undefined;
			const buttonMarginRight = this.overhangArray.includes('right')
				? `calc(-1 * var(--zds-button-inset-x-modified) - ${additionalPadding.right}px)`
				: undefined;

			this.buttonStyle = {
				borderTopLeftRadius: borderRadii.topLeft + 'px',
				borderBottomLeftRadius: borderRadii.bottomLeft + 'px',
				borderTopRightRadius: borderRadii.topRight + 'px',
				borderBottomRightRadius: borderRadii.bottomRight + 'px',
				marginLeft: buttonMarginLeft,
				marginRight: buttonMarginRight,
			};

			this.wrapperStyle = {
				paddingLeft: additionalPadding.left + 'px',
				paddingRight: additionalPadding.right + 'px',
			};

			this.isStyleReady = true;
		});
	}
}
